
import React, { useState, useEffect ,useMemo} from 'react';
import { ArrowLeft, X, AlertTriangle, Download, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { uploadImportChangeList,getCombinedChangeListsForImport,deleteImportChangeList ,cacheManager} from '../../services/apiService';
import { useAssessment } from './AssessmentContext';
import { decodedToken } from '../../utils/auth';

const ImportChangeList = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [userUploads, setUserUploads] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('info');
  const [modalDetails, setModalDetails] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const decoded = decodedToken();
  const userEmail = decoded?.email || '';
  const userType = decoded?.userType;
  const {
    currentTab,
    setCurrentTab,
    setChangeList,
    clearTabData,
    getTabData
  } = useAssessment();

  const tabs = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD' },
    { id: 'AVA', label: 'AVA' },
  ];
  // Add at the top with other state declarations (around line 28)
const subProductTypes = {
  PD: [
    { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
    { id: 'PD_RGB LED', label: 'PD_RGB LED' },
    { id: 'PD_AIO', label: 'PD_AIO' },
    { id: 'PD_EPD', label: 'PD_EPD' }
  ],
  AVA: [
    { id: 'AVA_Audio', label: 'AVA_Audio' },
    { id: 'AVA_Headphone', label: 'AVA_Headphone' }
  ]
};

const [selectedSubType, setSelectedSubType] = useState({});
const [showSubTypeDropdown, setShowSubTypeDropdown] = useState({});

// Determine effective product type
const effectiveProductType = useMemo(() => {
  const tab = tabs.find(t => t.id === currentTab);
  if (tab && subProductTypes[currentTab]) {
    return selectedSubType[currentTab] || subProductTypes[currentTab][0].id;
  }
  return currentTab;
}, [currentTab, selectedSubType]);


  // **NEW**: Cleanup function to reset state on unmount
  useEffect(() => {
    return () => {
      console.log('ImportChangeList unmounting - resetting state');
      // Reset all local state
      setUploadedFile(null);
      setDefaultTemplate(null);
      setUserUploads([]);
      setSelectedTemplate(null);
      setDownloadUrl(null);
      setSelectedUploadId(null);
      setLoading(false);
      setError(null);
      setShowModal(false);
      setModalMessage('');
      setModalType('info');
      setModalDetails(null);
    };
  }, []); // Empty dependency array - runs only on mount and unmount

  const truncateFilename = (filename, maxLength = 30) => {
    if (!filename || filename.length <= maxLength) return filename;

    const extension = filename.split('.').pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);

    return `${truncatedName}...${extension}`;
  };
const fetchChangeLists = async (tabId) => {
  if (!userEmail || !userType) {
    setError('User email and type not found. Please log in again.');
    return;
  }
  
  // **CALCULATE EFFECTIVE PRODUCT TYPE FIRST**
  const productTypeToFetch = selectedSubType[tabId] || 
    (subProductTypes[tabId] ? subProductTypes[tabId][0].id : tabId);
  
  console.log('Fetching change lists for product type:', productTypeToFetch);
  
  setLoading(true);
  setError(null);
  try {
    const cleanUserType = userType.trim();
   const response = await getCombinedChangeListsForImport(productTypeToFetch, userEmail, cleanUserType);
    
    console.log('Response from combined API:', response);
    
    if (response.success) {
      if (response.defaultTemplate) {
        console.log('Setting default template:', response.defaultTemplate);
        setDefaultTemplate(response.defaultTemplate);
        setDownloadUrl(response.defaultTemplate.s3Url);
        setSelectedTemplate('default');
      } else {
        console.log('No default template found');
        setDefaultTemplate(null);
        setDownloadUrl(null);  // **ADD THIS LINE**
        setSelectedTemplate(null);  // **ADD THIS LINE**
      }
      
      if (response.userUploads && response.userUploads.length > 0) {
        console.log('Setting user uploads:', response.userUploads.length);
        setUserUploads(response.userUploads);
      } else {
        console.log('No user uploads found');
        setUserUploads([]);
      }
    } else {
      console.log('Response not successful');
      setDefaultTemplate(null);
      setUserUploads([]);
      setDownloadUrl(null);
      setSelectedTemplate(null);  // **ADD THIS LINE**
    }
    
    const currentTabData = getTabData(tabId);
    setSelectedUploadId(currentTabData.changeListId || null);
    
  } catch (err) {
    console.error('Failed to load templates:', err);
    setError('Failed to load templates.');
    setDefaultTemplate(null);
    setUserUploads([]);
    setDownloadUrl(null);
    setSelectedTemplate(null);  // **ADD THIS LINE**
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchChangeLists(currentTab);
  }, [currentTab, userEmail, userType]);
  useEffect(() => {
    const { state } = location;
  if (!state) return;
  
  // Handle returnTab
  if (state.returnTab && state.returnTab !== currentTab) {
    console.log('Setting return tab:', state.returnTab);
      setCurrentTab(state.returnTab);
 }
  
  // **ADDED: Handle incoming sub-type**
  if (state.subType) {
    console.log('Received sub-type from navigation:', state.subType);
    
    // Check if this is a sub-type (contains underscore)
    const isSubType = state.subType.includes('_');
    const baseTab = isSubType ? state.subType.split('_')[0] : state.subType;
    
    // Set the sub-type if it's valid for this tab
    if (isSubType && subProductTypes[baseTab]) {
      console.log(`Setting sub-type: ${state.subType} for tab: ${baseTab}`);
      setSelectedSubType(prev => ({ ...prev, [baseTab]: state.subType }));
      
      // Fetch data for this specific sub-type
      if (userEmail && userType) {
        fetchChangeLists(baseTab);
      }
    }
    }
  }, [location.state, setCurrentTab]);

  useEffect(() => {
  // Fetch data when sub-type changes for PD or AVA
  if (subProductTypes[currentTab] && selectedSubType[currentTab]) {
    console.log('Sub-type changed, fetching data for:', selectedSubType[currentTab]);
    fetchChangeLists(currentTab);
  }
}, [selectedSubType[currentTab]]);
  const handleBackClick = () => {
    navigate('/hardware-change', {
      state: { activeTab: currentTab, returnFromImport: true,subType: effectiveProductType },
      replace: false
    });
  };
  const handleTemplateSelect = (template, type) => {
    console.log('Template selected:', type, template);
    setSelectedTemplate(type);
    setDownloadUrl(template.s3Url);
  };

  const handleDownloadTemplate = () => {
    if (downloadUrl) {
      console.log('Downloading template from:', downloadUrl);
      window.open(downloadUrl, '_blank');
    } else {
      alert('No template available to download.');
    }
  };
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('File selected:', file.name);
      setUploadedFile(file);
    }
  };
  const handleUploadClick = () => {
    document.getElementById('file-upload').click();
  };
const handleSubmit = async () => {
  if (!uploadedFile) return;
  if (!userEmail || !userType) {
    setError('User email and type not found. Please log in again.');
    return;
  }

 setLoading(true);
  setError(null);
  setModalDetails(null);
  const formData = new FormData();
  formData.append('file', uploadedFile);
  formData.append('productType', effectiveProductType);

    try {
      console.log('Uploading file:', uploadedFile.name, 'for', currentTab);
      const response = await uploadImportChangeList(formData, userEmail, userType);
      console.log('Upload response:', response);

      if (response.success === false) {
        let errorMessage = response.message || 'Upload failed';
        let details = null;
        if (response.error === 'Invalid Excel file') {
          if (response.expectedSheets && response.foundSheets) {
            details = {
              expected: response.expectedSheets,
              found: response.foundSheets
            };
          }
        }
        setModalType('error');
        setModalMessage(errorMessage);
        setModalDetails(details);
        setShowModal(true);
        return;
      }
      if (response.isDuplicate) {
        setModalType('duplicate');
        setModalMessage(
          `The uploaded file is identical to the current version. No changes detected.\n\nThe existing version will continue to be used.`
        );
        setShowModal(true);

      // **FIX**: Force cache clear and refresh
      cacheManager.invalidateProduct(currentTab, userEmail);
      await fetchChangeLists(currentTab);
      setUploadedFile(null);
    } else {
      // SUCCESS - New file uploaded
      setModalType('info');
      setModalMessage('Change list uploaded successfully!');
      setShowModal(true);
      
      // Clear the context data
      clearTabData(currentTab);
      setChangeList(currentTab, response.id, null, null);
      
      // **CRITICAL FIX**: Invalidate cache first, then update states
      cacheManager.invalidateProduct(currentTab, userEmail);
      
      // Update download URL immediately with the newly uploaded file's URL
      if (response.s3Url) {
        setDownloadUrl(response.s3Url);
        }

        await fetchChangeLists(currentTab);

      setUploadedFile(null);
      setSelectedUploadId(null);


        setTimeout(() => {
          navigate('/hardware-change', {
            state: {
              activeTab: currentTab,
              changeListId: response.id,
              returnFromImport: true,
              newUpload: true,
             subType: effectiveProductType
            }
          });
        }, 1500);
      }
    } catch (err) {
      console.error('Upload error:', err);
      let errorMessage = 'Failed to upload file';
      let details = null;
      if (err.response?.data) {
        const data = err.response.data;
        errorMessage = data.message || data.error || errorMessage;
        if (data.expectedSheets && data.foundSheets) {
          details = { expected: data.expectedSheets, found: data.foundSheets };
        }
      }
      setModalType('error');
      setModalMessage(errorMessage);
      setModalDetails(details);
      setShowModal(true);

      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };
  const handleSelectUpload = (upload) => {
    console.log('Selecting upload:', upload.id);
    clearTabData(currentTab);
    setChangeList(currentTab, upload.id, null, null);
    setSelectedUploadId(upload.id);
    navigate('/hardware-change', {
      state: {
        activeTab: currentTab,
        changeListId: upload.id,
        returnFromImport: true,
        subType: effectiveProductType
      },
      replace: false
    });
  };
const handleRemoveUpload = async (uploadId, e) => {
 e.stopPropagation();
    
    if (!userEmail) return;
    
  console.log('Removing upload:', uploadId);
  setLoading(true);
  try {
    await deleteImportChangeList(uploadId, userEmail);
    
      cacheManager.invalidateProduct(currentTab, userEmail);
    clearTabData(currentTab);
    setSelectedUploadId(null);
    
      await fetchChangeLists(currentTab);
  } catch (err) {
      console.error('Delete error:', err);
    setError('Failed to delete upload.');
  } finally {
    setLoading(false);
  }
};
const handleTabChange = (tabId) => {
  if (tabId !== currentTab) {
    console.log('Changing tab to:', tabId);
    // **ADD THESE LINES**
    setDownloadUrl(null);
    setSelectedTemplate(null);
    setDefaultTemplate(null);
    setUserUploads([]);
    setCurrentTab(tabId);
  }
};

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
    setModalDetails(null);
  };
  const getModalColor = () => {
    switch (modalType) {
      case 'duplicate': return 'bg-yellow-900/30 border-yellow-500';
      case 'error': return 'bg-red-900/30 border-red-500';
      case 'info':
      default: return 'bg-blue-900/30 border-blue-500';
    }
  };
  const getModalIcon = () => {
    if (modalType === 'error') return <AlertTriangle className="w-8 h-8 text-red-400" />;
    return null;
  };
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${getModalColor()} border p-6 rounded-lg max-w-md w-full shadow-2xl`}>
            <div className="flex items-start gap-3 mb-4">
              {getModalIcon()}
              <h3 className="text-lg font-semibold text-white">
                {modalType === 'duplicate' ? 'Duplicate Detected' :
                 modalType === 'error' ? 'Invalid File' : 'Information'}
              </h3>
            </div>
            <p className="text-sm text-gray-300 whitespace-pre-line mb-4">{modalMessage}</p>
            {modalDetails && (
              <div className="mt-4 p-3 bg-gray-900/50 rounded border border-gray-700 text-xs">
                <div className="mb-2">
                  <span className="font-semibold text-gray-300">Expected sheets:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {modalDetails.expected.map(sheet => (
                      <span key={sheet} className="px-2 py-1 bg-green-900/30 text-green-300 rounded">{sheet}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="font-semibold text-gray-300">Found in your file:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {modalDetails.found.map(sheet => (
                      <span key={sheet} className="px-2 py-1 bg-red-900/30 text-red-300 rounded">{sheet}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  modalType === 'duplicate' ? 'bg-yellow-600 hover:bg-yellow-500' :
                  modalType === 'error' ? 'bg-red-600 hover:bg-red-500' :
                  'bg-blue-600 hover:bg-blue-500'
                } text-white`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
        <div className="flex items-center px-4 py-3 sm:px-6">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-10 h-10 -ml-2 mr-2 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors touch-manipulation"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-white" />
          </button>
          <h1 className="text-base font-medium sm:text-lg">HW Change Assessment - Import for {currentTab}</h1>
        </div>
      </div>
      {/* Content */}
      <div className="px-4 py-6 sm:px-6 sm:py-8 max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-normal text-white sm:text-3xl md:text-4xl mb-6">
            Import Change List for {currentTab}
          </h2>
        </div>
        {/* Tabs */}
        <div className="mb-8">
          <div className="flex border-b border-gray-700 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-shrink-0 px-6 py-4 text-sm font-medium border-b-2 transition-all touch-manipulation
                  ${currentTab === tab.id
                    ? 'border-blue-500 text-white bg-blue-500/10'
                    : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/50'
                  } ${index < tabs.length - 1 ? 'mr-6 sm:mr-8' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-8">
{/* Sub-type Dropdown for PD and AVA */}
{subProductTypes[currentTab] && (
  <div className="mb-6">
    <div className="relative inline-block w-64">
      <button
        onClick={() => setShowSubTypeDropdown(prev => ({ 
          ...prev, 
          [currentTab]: !prev[currentTab] 
        }))}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-800 
                 text-white rounded-lg border border-gray-600 hover:bg-gray-700 
                 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      >
        <span className="text-sm">
          {subProductTypes[currentTab]?.find(st => st.id === effectiveProductType)?.label || 'Select Type'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${
          showSubTypeDropdown[currentTab] ? 'rotate-180' : ''
        }`} />
      </button>
      
      {showSubTypeDropdown[currentTab] && (
        <div className="absolute z-30 w-full mt-1 bg-gray-800 border border-gray-600 
                      rounded-lg shadow-xl max-h-60 overflow-auto">
          {subProductTypes[currentTab]?.map(subType => (
            <button
              key={subType.id}
              onClick={() => {
                setSelectedSubType(prev => ({ ...prev, [currentTab]: subType.id }));
                setShowSubTypeDropdown(prev => ({ ...prev, [currentTab]: false }));
                // Clear existing data
                setDefaultTemplate(null);
                setUserUploads([]);
                setDownloadUrl(null);
                setSelectedTemplate(null);
              }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 
                       ${effectiveProductType === subType.id 
                         ? 'bg-blue-600 text-white' 
                         : 'text-gray-300'}`}
            >
              {subType.label}
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
)}
          {/* Download Template */}
          <div>
  {/* Show Selected Template Name Above */}
  {downloadUrl && selectedTemplate && (
    <div className="text-sm text-gray-300 mb-3">
      <span className="font-medium">
        {selectedTemplate === 'default'
          ? truncateFilename(defaultTemplate?.filename || 'Default Template', 50)
          : truncateFilename(
              userUploads.find(u => u.id === selectedTemplate)?.filename || 'Template',
              50
            )
        }
      </span>
    </div>
  )}
  
  {/* Button - Text Remains Same */}
            <button
              onClick={handleDownloadTemplate}
              disabled={!downloadUrl}
              className={`px-8 py-4 rounded-xl text-white font-medium transition-all shadow-lg hover:shadow-xl ${
                downloadUrl 
                  ? 'bg-blue-600/80 hover:bg-blue-600' 
                  : 'bg-gray-600/50 cursor-not-allowed'
              }`}
            >
              Download Current Template
            </button>
            {!downloadUrl && !loading && (
              <p className="text-sm text-gray-400 mt-2">No template available</p>
            )}
          </div>
          {/* Upload Section */}
          <div className="space-y-4">
            <button
              onClick={handleUploadClick}
              className="px-8 py-4 bg-gray-700/80 hover:bg-gray-700 rounded-xl text-white font-medium transition-all shadow-lg hover:shadow-xl"
            >
              Upload New File
            </button>
            <input 
              id="file-upload" 
              type="file" 
              accept=".xlsx,.xls,.csv,.xlsm" 
              onChange={handleFileUpload} 
              className="hidden" 
            />
            <div className="w-full max-w-2xl">
              <div className="min-h-[80px] bg-gray-800/50 border-2 border-dashed border-gray-600 rounded-xl p-6 flex items-center justify-center">
                {uploadedFile ? (
                  <div className="text-center">
                    <div className="text-white font-medium" title={uploadedFile.name}>
                      {truncateFilename(uploadedFile.name)}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">No file selected</div>
                )}
              </div>
            </div>
          </div>
          {/* Latest Upload - SIMPLIFIED */}
          <div className="space-y-4">
            <h3 className="text-xl font-medium text-white">Default Template</h3>
            {loading ? (
              <p className="text-gray-400">Loading...</p>
            ) : error ? (
              <p className="text-red-400">{error}</p>
            ) : !defaultTemplate ? (
              <p className="text-gray-400">No default template available for {currentTab}.</p>
            ) : (
              <div 
                onClick={() => handleTemplateSelect(defaultTemplate, 'default')}
                className={`flex justify-between items-center p-4 rounded-lg border cursor-pointer transition-all ${
                selectedTemplate === 'default'
                  ? 'bg-blue-900/20 border-blue-500'
                  : 'bg-gray-800/50 border-gray-600 hover:border-gray-500'
              }`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate mb-2" title={defaultTemplate.filename}>
                    {defaultTemplate.filename}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {new Date(defaultTemplate.uploadDate).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                {selectedTemplate === 'default' && (
                  <div className="ml-4">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. My Uploads - FOURTH */}
          <div className="space-y-4">
            <h3 className="text-xl font-medium text-white">My Uploads</h3>
            {loading ? (
              <p className="text-gray-400">Loading...</p>
            ) : userUploads.length === 0 ? (
              <p className="text-gray-400">No uploads found for {effectiveProductType}.</p>
            ) : (
              <div className="space-y-2">
                {userUploads.slice(0, 1).map((upload) => (
                  <div 
                    key={upload.id}
                    onClick={() => handleTemplateSelect(upload, upload.id)}
                    className={`flex justify-between items-center p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedTemplate === upload.id
                        ? 'bg-blue-900/20 border-blue-500'
                        : 'bg-gray-800/50 border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate mb-2" title={upload.filename}>
                        {upload.filename}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {new Date(upload.uploadDate).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {selectedTemplate === upload.id && (
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      )}
                  {selectedUploadId !== upload.id && (
                    <button
                      onClick={(e) => {
                            e.stopPropagation();
                            handleSelectUpload(upload);
                          }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      Use This
                    </button>
                  )}
                  <button
                    onClick={(e) => handleRemoveUpload(upload.id, e)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                    aria-label="Remove upload"
                  >
                    <X size={20} />
                  </button>
                </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-4 pt-8 justify-end">
            <button
              onClick={handleBackClick}
              className="w-full sm:w-auto px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-xl text-white font-medium transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={!uploadedFile || loading}
              className={`w-full sm:w-auto px-8 py-4 rounded-xl font-medium transition-all
                ${uploadedFile && !loading
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-blue-600/50 text-white/70 cursor-not-allowed'
                }`}
            >
              {loading ? 'Uploading...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportChangeList;
